import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { lockBodyScroll, unlockBodyScroll } from "../core/scroll-lock";
import { adminUpdatePartnerConfig } from "../core/axios/endpoints";
import { SUBSIDIE_MODULES, MODULE_LABELS } from "../core/partner-subsidie";
import PriceScopeChoice, { PRICE_SCOPE_OPEN } from "./price-scope-choice";

// Portal-modal voor admins om een klant als BTW-partner te activeren of de
// partner-instellingen aan te passen. Vervangt het oude env-driven seed
// patroon - alle partner-configuratie verloopt vanaf nu via de Klanten-UI.

const slugify = (input) =>
  String(input ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);

const PUBLIC_BASE_URL = "https://subsidie2gether.nl";

const PartnerConfigModal = ({ isOpen, user, onClose, onSaved }) => {
  const existingBtwFee =
    user?.applicationFees?.btwBatterij ?? user?.btwPricePerApplication ?? 65;

  const [enabled, setEnabled] = useState(false);
  const [partnerSlug, setPartnerSlug] = useState("");
  const [partnerLogoUrl, setPartnerLogoUrl] = useState("");
  const [billingEmail, setBillingEmail] = useState("");
  const [btwPrice, setBtwPrice] = useState(65);
  // Subsidie-partnerflow: per regeling aan/uit met eigen prijs per aanvraag.
  const [partnerModules, setPartnerModules] = useState([]);
  const [fees, setFees] = useState({ eia: 250, eiaZon: 250, isde: 250 });
  const [priceScope, setPriceScope] = useState(PRICE_SCOPE_OPEN);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    lockBodyScroll();
    const onKey = (e) => {
      if (e.key === "Escape" && !saving) onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      unlockBodyScroll();
    };
  }, [isOpen, saving, onClose]);

  useEffect(() => {
    if (!isOpen) {
      setError(null);
      setSaved(null);
      return;
    }
    setEnabled(!!user?.btwPartnerEnabled);
    setPartnerSlug(
      user?.partnerSlug ?? slugify(user?.bedrijfsnaam ?? user?.naam ?? ""),
    );
    setPartnerLogoUrl(user?.partnerLogoUrl ?? "");
    setBillingEmail(user?.billingEmail ?? user?.email ?? "");
    setBtwPrice(existingBtwFee);
    setPartnerModules(
      Array.isArray(user?.partnerModules)
        ? user.partnerModules.filter((m) => SUBSIDIE_MODULES.includes(m))
        : [],
    );
    setFees({
      eia: user?.applicationFees?.eia ?? 250,
      eiaZon: user?.applicationFees?.eiaZon ?? 250,
      isde: user?.applicationFees?.isde ?? 250,
    });
    setPriceScope(PRICE_SCOPE_OPEN);
    setError(null);
    setSaved(null);
  }, [isOpen, user, existingBtwFee]);

  // Alleen een prijs die al gold en nu anders is telt als wijziging; een
  // module die net aangezet wordt heeft nog geen aanvragen.
  const priceChanged =
    (enabled && !!user?.btwPartnerEnabled && Number(btwPrice) !== existingBtwFee) ||
    partnerModules.some(
      (m) =>
        (user?.partnerModules ?? []).includes(m) &&
        Number(fees[m]) !== (user?.applicationFees?.[m] ?? 250),
    );

  const hasSubsidieModule = partnerModules.length > 0;
  const anyPartner = enabled || hasSubsidieModule;

  const toggleModule = (m) =>
    setPartnerModules((prev) =>
      prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m],
    );

  const partnerUrl = useMemo(() => {
    if (!partnerSlug) return "";
    return `${PUBLIC_BASE_URL}/btw/p/${slugify(partnerSlug)}`;
  }, [partnerSlug]);

  const subsidiePartnerUrl = useMemo(() => {
    if (!partnerSlug) return "";
    return `${PUBLIC_BASE_URL}/subsidie/p/${slugify(partnerSlug)}`;
  }, [partnerSlug]);

  const submit = async () => {
    if (!user?._id) return;
    setSaving(true);
    setError(null);
    try {
      // Slug, logo en billing-e-mail gelden voor beide flows (BTW en
      // subsidie), dus die gaan ook mee als alleen subsidie-modules aan staan.
      const feesPayload = {};
      partnerModules.forEach((m) => {
        const v = Number(fees[m]);
        if (Number.isFinite(v) && v >= 0) feesPayload[m] = v;
      });
      const payload = {
        enabled,
        partnerModules,
        fees: feesPayload,
        ...(priceChanged ? { priceScope } : {}),
        ...(anyPartner
          ? {
              partnerSlug: slugify(partnerSlug),
              partnerLogoUrl: partnerLogoUrl || undefined,
              billingEmail: billingEmail || undefined,
            }
          : {}),
        ...(enabled
          ? {
              btwPriceExclVat:
                typeof btwPrice === "number" && btwPrice >= 0
                  ? btwPrice
                  : undefined,
            }
          : {}),
      };
      const res = await adminUpdatePartnerConfig(user._id, payload);
      const updatedUser = res?.data ?? res;
      setSaved(updatedUser);
      onSaved?.(updatedUser);
      // Partner aan/uit maakt of archiveert een bord; sidebar verversen.
      window.dispatchEvent(new Event("boards:changed"));
    } catch (e) {
      setError(
        e?.response?.data?.error ?? e?.message ?? "Opslaan mislukt.",
      );
    } finally {
      setSaving(false);
    }
  };

  const copyUrl = async (url = partnerUrl) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // clipboard kan geblokkeerd zijn - dan is de URL handmatig kopieerbaar.
    }
  };

  if (!isOpen) return null;
  if (typeof document === "undefined") return null;

  const customerName =
    user?.bedrijfsnaam || user?.naam || user?.email || "Klant";

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-start justify-center overflow-y-auto bg-[#091E428A] p-4">
      <div className="my-8 w-full max-w-2xl rounded-md bg-white shadow-overlay">
        <div className="flex items-start justify-between gap-4 px-6 pt-6">
          <div>
            <h2 className="text-xl font-medium text-ink">
              Partner instellen
            </h2>
            <p className="mt-1 text-sm text-ink-subtlest">
              <strong>{customerName}</strong>{" "}
              {anyPartner
                ? `wordt partner voor ${[
                    enabled ? "BTW Batterij" : null,
                    ...partnerModules.map((m) => MODULE_LABELS[m]),
                  ]
                    .filter(Boolean)
                    .join(", ")}.`
                : "is geen partner."}{" "}
              Partners zien in hun portaal automatisch de bijbehorende overzichten.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded text-ink-subtlest hover:bg-[#091E420F] disabled:opacity-50"
            aria-label="Sluiten"
          >
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M4.3 4.3a1 1 0 011.4 0L10 8.6l4.3-4.3a1 1 0 111.4 1.4L11.4 10l4.3 4.3a1 1 0 11-1.4 1.4L10 11.4l-4.3 4.3a1 1 0 11-1.4-1.4L8.6 10 4.3 5.7a1 1 0 010-1.4z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        <div className="space-y-5 px-6 py-4 text-sm text-ink">
          {/* Enable-toggle */}
          <label className="flex items-start gap-3 rounded bg-sunken p-4">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              disabled={saving}
              className="mt-0.5 h-4 w-4 rounded border-line text-brand focus:ring-0"
            />
            <div className="flex-1">
              <p className="text-sm font-medium text-ink">
                BTW Batterij partner
              </p>
              <p className="mt-0.5 text-xs text-ink-subtlest">
                Klanten dienen via /btw/p/slug kosteloos een BTW-teruggave in.
                Bij uitschakelen blijven de instellingen bewaard, maar de
                BTW-partnerpagina werkt niet meer.
              </p>
            </div>
          </label>

          {/* Subsidie-modules */}
          <div className="rounded bg-sunken p-4">
            <p className="text-sm font-medium text-ink">
              Subsidie-partner (aanvraag via partner-link, klant betaalt niets)
            </p>
            <p className="mt-0.5 text-xs text-ink-subtlest">
              Per regeling aan te zetten. Na goedkeuring komt de aanvraag als
              regel op de partner-factuur voor het bedrag hieronder.
            </p>
            <div className="mt-3 space-y-2">
              {SUBSIDIE_MODULES.map((m) => {
                const on = partnerModules.includes(m);
                return (
                  <div key={m} className="flex flex-wrap items-center gap-3">
                    <label className="flex w-32 items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => toggleModule(m)}
                        disabled={saving}
                        className="h-4 w-4 rounded border-line text-brand focus:ring-0"
                      />
                      {MODULE_LABELS[m]}
                    </label>
                    {on ? (
                      <div className="relative w-40">
                        <span className="absolute inset-y-0 left-2.5 flex items-center text-xs text-ink-subtlest">
                          €
                        </span>
                        <input
                          type="number"
                          min={0}
                          step={5}
                          value={fees[m]}
                          onChange={(e) =>
                            setFees((f) => ({ ...f, [m]: Number(e.target.value) }))
                          }
                          disabled={saving}
                          className="w-full rounded border-2 border-line bg-white py-1.5 pl-7 pr-2 text-sm text-ink focus:border-brand focus:outline-none disabled:opacity-50"
                        />
                        <span className="absolute inset-y-0 right-2 flex items-center text-[10px] text-ink-subtlest">
                          excl. btw
                        </span>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>

          {anyPartner ? (
            <>
              <label className="block text-sm">
                <span className="text-xs font-semibold text-ink-subtlest">
                  Partner-slug (URL-segment)
                </span>
                <div className="mt-1 flex items-stretch overflow-hidden rounded border-2 border-line bg-white focus-within:border-brand">
                  <span className="flex items-center bg-sunken px-2.5 text-xs text-ink-subtlest">
                    {enabled ? "/btw/p/" : "/subsidie/p/"}
                  </span>
                  <input
                    type="text"
                    value={partnerSlug}
                    onChange={(e) => setPartnerSlug(slugify(e.target.value))}
                    disabled={saving}
                    placeholder="solar-centrum-nederland"
                    className="flex-1 bg-white px-2 py-1.5 text-sm text-ink placeholder:text-ink-subtlest focus:outline-none disabled:opacity-50"
                  />
                </div>
                <p className="mt-1 text-xs text-ink-subtlest">
                  Alleen lowercase letters, cijfers en streepjes. Moet uniek
                  zijn.
                </p>
              </label>

              {subsidiePartnerUrl && hasSubsidieModule ? (
                <div className="flex items-center gap-2 rounded bg-[#F3F0FF] px-3 py-2 text-xs text-[#5E4DB2]">
                  <span className="shrink-0 font-medium">Subsidie</span>
                  <span className="flex-1 truncate">{subsidiePartnerUrl}</span>
                  <button
                    type="button"
                    onClick={() => copyUrl(subsidiePartnerUrl)}
                    className="rounded bg-white px-2 py-0.5 font-medium text-[#5E4DB2] shadow-card hover:bg-[#091E420F]"
                  >
                    Kopieer
                  </button>
                </div>
              ) : null}

              {partnerUrl && enabled ? (
                <div className="flex items-center gap-2 rounded bg-[#DCFFF1] px-3 py-2 text-xs text-[#1F845A]">
                  <svg className="h-3.5 w-3.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" fill="none" />
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.71-9.29a1 1 0 00-1.42-1.42L9 10.59 7.71 9.29a1 1 0 00-1.42 1.42l2 2a1 1 0 001.42 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="flex-1 truncate">{partnerUrl}</span>
                  <button
                    type="button"
                    onClick={() => copyUrl(partnerUrl)}
                    className="rounded bg-white px-2 py-0.5 font-medium text-[#1F845A] shadow-card hover:bg-[#091E420F]"
                  >
                    Kopieer
                  </button>
                </div>
              ) : null}

              <label className="block text-sm">
                <span className="text-xs font-semibold text-ink-subtlest">Logo-URL</span>
                <input
                  type="url"
                  value={partnerLogoUrl}
                  onChange={(e) => setPartnerLogoUrl(e.target.value)}
                  disabled={saving}
                  placeholder="https://.../logo.png"
                  className="mt-1 w-full rounded border-2 border-line bg-white px-2 py-1.5 text-sm text-ink placeholder:text-ink-subtlest focus:border-brand focus:outline-none disabled:opacity-50"
                />
                <p className="mt-1 text-xs text-ink-subtlest">
                  Wordt rechts in de header van het partner-formulier getoond.
                </p>
              </label>

              <label className="block text-sm">
                <span className="text-xs font-semibold text-ink-subtlest">
                  Billing-e-mail (waar partner-facturen heen gaan)
                </span>
                <input
                  type="email"
                  value={billingEmail}
                  onChange={(e) => setBillingEmail(e.target.value)}
                  disabled={saving}
                  placeholder="facturen@partner.nl"
                  className="mt-1 w-full rounded border-2 border-line bg-white px-2 py-1.5 text-sm text-ink placeholder:text-ink-subtlest focus:border-brand focus:outline-none disabled:opacity-50"
                />
              </label>

              {enabled ? (
              <label className="block text-sm">
                <span className="text-xs font-semibold text-ink-subtlest">
                  BTW-prijs per aanvraag (excl. btw)
                </span>
                <div className="relative mt-1">
                  <span className="absolute inset-y-0 left-2.5 flex items-center text-sm text-ink-subtlest">
                    €
                  </span>
                  <input
                    type="number"
                    min={0}
                    step={5}
                    value={btwPrice}
                    onChange={(e) => setBtwPrice(Number(e.target.value))}
                    disabled={saving}
                    className="w-full rounded border-2 border-line bg-white py-1.5 pl-7 pr-2 text-sm text-ink focus:border-brand focus:outline-none disabled:opacity-50"
                  />
                </div>
                <p className="mt-1 text-xs text-ink-subtlest">
                  Bedrag dat we per BTW-aanvraag aan de partner factureren.
                </p>
              </label>
              ) : null}
            </>
          ) : null}

          {priceChanged && !saved ? (
            <PriceScopeChoice
              value={priceScope}
              onChange={setPriceScope}
              disabled={saving}
            />
          ) : null}

          {error ? (
            <div className="rounded bg-[#FFECEB] px-3 py-2 text-sm text-[#AE2A19]">
              {error}
            </div>
          ) : null}

          {saved ? (
            <div className="rounded bg-[#DCFFF1] px-3 py-2 text-sm text-[#1F845A]">
              {anyPartner
                ? `Opgeslagen. ${
                    [enabled ? partnerUrl : null, hasSubsidieModule ? subsidiePartnerUrl : null]
                      .filter(Boolean)
                      .map((u) => `Partner-pagina: ${u}`)
                      .join(" · ")
                  }`
                : "Partner-status uitgezet."}
            </div>
          ) : null}
        </div>

        <div className="flex items-center justify-end gap-2 px-6 pb-6 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="h-8 rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224] disabled:cursor-not-allowed disabled:opacity-50"
          >
            Sluiten
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving || (anyPartner && !partnerSlug)}
            className="h-8 rounded bg-brand px-3 text-sm font-medium text-white hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? "Opslaan…" : "Opslaan"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default PartnerConfigModal;
